import * as React from 'react';
import { useRef } from 'react';
import { makeStyles } from '@fluentui/react-components';

interface ExpandableCardProps {
  cardKey: string;
  title: string;
  date: string;
  city: string;
  children: React.ReactNode;
  expandedCards: { [key: string]: boolean };
  toggleCard: (cardKey: string) => void;
}

const useStyles = makeStyles({
  card: {
    background: 'rgba(255, 253, 248, 0.95)',
    padding: '1.5rem',
    borderRadius: '10px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    flex: '1 1 300px',
    minWidth: '300px',
    maxWidth: '350px',
    alignSelf: 'flex-start',
    overflow: 'hidden',
    selectors: {
      '&:hover': {
        transform: 'translateY(-5px)',
        background: 'rgba(255, 253, 248, 0.98)',
      },
    },
    '@media (max-width: 768px)': {
      minWidth: 'unset',
      maxWidth: 'none',
      width: '100%',
      margin: '0 auto',
    },
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.5rem',
  },
  cardTitle: {
    margin: 0,
    fontSize: '22px',
    color: 'var(--primary-color)',
    marginBottom: '6px',
  },
  cardTime: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: '#6a8ca3',
  },
  expandIcon: {
    fontSize: '18px',
    color: 'var(--primary-color)',
    transition: 'transform 0.3s ease',
  },
  expandIconRotated: {
    transform: 'rotate(180deg)',
  },
  cardContent: {
    overflow: 'hidden',
    transition: 'all 0.3s ease',
  },
  directionsLink: {
    display: 'inline-block',
    marginTop: '1rem',
    padding: '0.5rem 1rem',
    backgroundColor: 'var(--primary-color)',
    color: '#fff8e1',
    textDecoration: 'none',
    borderRadius: '5px',
    fontWeight: 600,
    transition: 'background-color 0.3s ease',
    selectors: {
      '&:hover': {
        backgroundColor: 'var(--primary-hover)',
      },
    },
  },
  muted: {
    fontSize: '1rem',
    color: '#6a8ca3',
  },
});

const ExpandableCard: React.FC<ExpandableCardProps> = ({ 
  cardKey, 
  title, 
  date,
  city, 
  children,
  expandedCards,
  toggleCard
}) => {
  const classes = useStyles();
  const contentRef = useRef<HTMLDivElement>(null);
  const isExpanded = expandedCards[cardKey];
  
  return (
    <article className={classes.card} onClick={() => toggleCard(cardKey)}>
      <div className={classes.cardHeader}>
        <div>
          <h3 className={classes.cardTitle}>{title}</h3>
          <p className={classes.cardTime}>{date}</p>
          <p className={classes.cardTime}>{city}</p>
        </div>
        <span className={`${classes.expandIcon} ${isExpanded ? classes.expandIconRotated : ''}`}>
          ▼
        </span>
      </div>
      <div 
        className={classes.cardContent}
        style={{
          maxHeight: isExpanded ? undefined : '0px',
          opacity: isExpanded ? 1 : 0,
          marginTop: isExpanded ? '1rem' : '0px',
          transition: 'all 0.3s ease'
        }}
      >
        <div ref={contentRef}>
          {children}
        </div>
      </div>
    </article>
  );
};

export default ExpandableCard;